/**
 * Shim for the subset of `react-native-paper` used by the app:
 * Provider, TextInput (+ TextInput.Icon), IconButton, Button, FAB, Switch.
 *
 * Components accept a `className` (injected by styled-components) and an RN
 * `style` prop, both applied to the rendered DOM so styled() wrappers work.
 */
import React, { useState } from 'react';
import { toCss } from '../web/styleUtils';
import VectorIcon from './Icon';
import * as Colors from '../helpers/Colors';

/* ----------------------------- Provider ---------------------------------- */

export const Provider = function PaperProvider({ children }: { children?: React.ReactNode }) {
  return <>{children}</>;
};
export const DefaultTheme = { colors: {} };
export const configureFonts = () => ({});

/* ----------------------------- TextInput --------------------------------- */

interface TextInputIconProps {
  name?: string;
  icon?: string | (() => React.ReactNode);
  onPress?: () => void;
  color?: string;
  size?: number;
}

const TextInputIcon = function TextInputIcon({ name, icon, onPress, color, size = 24 }: TextInputIconProps) {
  if (typeof icon === 'function') {
    return (
      <span onClick={onPress} style={{ cursor: onPress ? 'pointer' : 'inherit', display: 'inline-flex' }}>
        {icon()}
      </span>
    );
  }
  const glyph = (icon as string) ?? name ?? '';
  return <VectorIcon name={glyph} size={size} color={color ?? Colors.secondaryText} onPress={onPress} />;
};

export interface PaperTextInputProps {
  value?: string;
  onChangeText?: (text: string) => void;
  label?: string;
  placeholder?: string;
  mode?: 'flat' | 'outlined';
  disabled?: boolean;
  editable?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
  right?: React.ReactNode;
  left?: React.ReactNode;
  onFocus?: () => void;
  onBlur?: () => void;
  onPressIn?: () => void;
  keyboardType?: string;
  secureTextEntry?: boolean;
  dense?: boolean;
  outlineColor?: string;
  theme?: any;
  style?: any;
  className?: string;
  showSoftInputOnFocus?: boolean;
  autoFocus?: boolean;
  [key: string]: any;
}

const TextInputBase = function PaperTextInput({
  value,
  onChangeText,
  label,
  placeholder,
  mode = 'flat',
  disabled,
  editable = true,
  multiline,
  numberOfLines,
  right,
  left,
  onFocus,
  onBlur,
  onPressIn,
  keyboardType,
  secureTextEntry,
  dense,
  outlineColor,
  theme,
  style,
  className,
  showSoftInputOnFocus,
  autoFocus,
  ...rest
}: PaperTextInputProps) {
  const [focused, setFocused] = useState(false);
  const readOnly = !editable || showSoftInputOnFocus === false;
  const textColor = theme?.colors?.text ?? Colors.secondaryText;
  const borderColor = focused ? theme?.colors?.primary ?? Colors.secondary : outlineColor ?? Colors.inactive;

  // Strip RN-only props that are invalid on DOM input/textarea elements.
  const { caretHidden: _ch, scrollEnabled: _se, autoCorrect: _ac, ...domRest } = rest as any;

  const InputTag: any = multiline ? 'textarea' : 'input';

  return (
    <div
      className={className}
      onMouseDown={onPressIn}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        border: mode === 'outlined' ? `1px solid ${borderColor}` : 'none',
        borderBottom: mode === 'outlined' ? `1px solid ${borderColor}` : `1px solid ${borderColor}`,
        borderRadius: mode === 'outlined' ? 4 : 0,
        padding: dense ? '6px 8px' : '10px 12px',
        background: disabled ? Colors.disabledBackground : '#fff',
        position: 'relative',
        boxSizing: 'border-box',
        ...(toCss(style) as any),
      }}
    >
      {left}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {label ? (
          <label style={{ fontSize: 11, color: Colors.secondaryText, marginBottom: 2 }}>{label}</label>
        ) : null}
        <InputTag
          value={value ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          rows={multiline ? numberOfLines ?? 3 : undefined}
          type={secureTextEntry ? 'password' : 'text'}
          inputMode={keyboardType === 'numeric' || keyboardType === 'number-pad' ? 'numeric' : undefined}
          onChange={(e: any) => onChangeText?.(e.target.value)}
          onFocus={() => {
            setFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          style={{
            border: 'none',
            outline: 'none',
            background: 'transparent',
            font: 'inherit',
            color: textColor,
            width: '100%',
            resize: 'none',
            padding: 0,
          }}
          {...domRest}
        />
      </div>
      {right}
    </div>
  );
};

export const TextInput = Object.assign(TextInputBase, { Icon: TextInputIcon, Affix: TextInputIcon });

/* ----------------------------- IconButton -------------------------------- */

export interface IconButtonProps {
  icon: string | (() => React.ReactNode);
  size?: number;
  color?: string;
  iconColor?: string;
  disabled?: boolean;
  onPress?: () => void;
  style?: any;
  className?: string;
  rippleColor?: string;
  [key: string]: any;
}

export const IconButton = function IconButton({
  icon,
  size = 24,
  color,
  iconColor,
  disabled,
  onPress,
  style,
  className,
}: IconButtonProps) {
  return (
    <button
      className={className}
      disabled={disabled}
      onClick={onPress}
      style={{
        border: 'none',
        background: 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        padding: 6,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(toCss(style) as any),
      }}
    >
      {typeof icon === 'function' ? (
        icon()
      ) : (
        <VectorIcon name={icon} size={size} color={iconColor ?? color ?? '#000'} />
      )}
    </button>
  );
};

/* ------------------------------- Button ---------------------------------- */

export interface PaperButtonProps {
  mode?: 'text' | 'outlined' | 'contained';
  onPress?: () => void;
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  compact?: boolean;
  uppercase?: boolean;
  color?: string;
  labelStyle?: any;
  contentStyle?: any;
  style?: any;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}

export const Button = function Button({
  mode = 'text',
  onPress,
  disabled,
  loading,
  icon,
  compact,
  uppercase,
  color,
  labelStyle,
  contentStyle,
  style,
  className,
  children,
}: PaperButtonProps) {
  const isContained = mode === 'contained';
  const label = toCss(labelStyle) as any;
  return (
    <button
      className={className}
      disabled={disabled || loading}
      onClick={onPress}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        border: mode === 'outlined' ? `1px solid ${color ?? Colors.secondary}` : 'none',
        borderRadius: 4,
        background: isContained ? color ?? Colors.secondary : 'transparent',
        color: isContained ? '#fff' : color ?? Colors.secondary,
        padding: compact ? '4px 8px' : '8px 16px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        textTransform: uppercase ? 'uppercase' : 'none',
        fontWeight: 600,
        ...(toCss(contentStyle) as any),
        ...(toCss(style) as any),
      }}
    >
      {icon ? <VectorIcon name={icon} size={18} color={isContained ? '#fff' : color ?? Colors.secondary} /> : null}
      <span style={label}>{children}</span>
    </button>
  );
};

/* --------------------------------- FAB ----------------------------------- */

export interface FABProps {
  icon?: string;
  label?: string;
  onPress?: () => void;
  disabled?: boolean;
  color?: string;
  small?: boolean;
  style?: any;
  className?: string;
  uppercase?: boolean;
  [key: string]: any;
}

export const FAB = function FAB({ icon, label, onPress, disabled, color, style, className }: FABProps) {
  return (
    <button
      className={className}
      disabled={disabled}
      onClick={onPress}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        border: 'none',
        borderRadius: label ? 28 : '50%',
        padding: label ? '14px 20px' : 16,
        background: '#00a3e0',
        color: color ?? '#fff',
        cursor: disabled ? 'default' : 'pointer',
        boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
        fontSize: 16,
        ...(toCss(style) as any),
      }}
    >
      {icon ? <VectorIcon name={icon} size={22} color={color ?? '#fff'} /> : null}
      {label ? <span>{label}</span> : null}
    </button>
  );
};

/* ------------------------------- Switch ---------------------------------- */

export { Switch } from '../web/react-native';

/* ------------------------------- Portal ---------------------------------- */

export const Portal = function Portal({ children }: { children?: React.ReactNode }) {
  return <>{children}</>;
};
(Portal as any).Host = function PortalHost({ children }: { children?: React.ReactNode }) {
  return <>{children}</>;
};

export default {
  Provider,
  DefaultTheme,
  TextInput,
  IconButton,
  Button,
  FAB,
  Portal,
};
